/**
 * 登录态失效（Auth 过期 / token 异常）统一处理
 *
 * 后端会以两种形式告知登录态失效：
 *   1) HTTP 状态码 401
 *   2) HTTP 200，但响应体里带 code = 401（如 { code: 401, data: 'token异常', message: '暂未登陆或token已经过期' }）
 * 两种情况都要清空登录信息并跳回登录页。
 *
 * 使用：在任意 axios 实例（含 axios 默认实例）上注册一次即可
 *   import { registerAuthExpired } from '@/utils/authExpired';
 *   registerAuthExpired(axios);          // 默认实例：覆盖页面里直接 import axios 的调用
 *   registerAuthExpired(http);           // 自定义实例：@/utils/request
 */
import Element from 'element-ui';
import UserInfoUtils from './userInfoUtils';

// 登录相关接口：这些接口返回 401 只表示账号/密码错误，由登录页自己提示，不能全局跳转
const AUTH_FREE_APIS = ['api/login', 'api/getPublicKey', 'api/register'];

export function isAuthFreeApi(url) {
  return AUTH_FREE_APIS.some(a => String(url || '').includes(a));
}

/**
 * 判定响应体是否为「登录态失效」
 * 命中：code / status = 401，或 message 明确提示 auth / token / 登录 过期失效
 */
export function isAuthExpiredBody(data) {
  if (!data || typeof data !== 'object') return false;
  if (Number(data.code) === 401 || Number(data.status) === 401) return true;
  // 少数接口把结论放在 data 字段，如 data: "token异常"
  if (/token.*(异常|失效|过期)|未登录|暂未登陆/i.test(String(data.data || ''))) return true;
  const msg = String(data.message || data.msg || '');
  return /auth|token|登录/i.test(msg) && /过期|失效|无效|expired|invalid/i.test(msg);
}

// 后端有时把包体以字符串返回，这里尝试解析后再判定
function parseBody(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  return null;
}

// 并发多个请求同时 401 时，只提示一次、只跳一次
let redirecting = false;

export function redirectToLogin(msg) {
  if (redirecting) return;
  redirecting = true;

  const tip = msg || '暂未登录或登录已过期，请重新登录';
  console.warn('[Auth] 登录态失效：' + tip + '，即将跳转登录页');

  // 只清登录态，保留「记住密码」保存的账号（userCode / userName / userPwd）
  try { UserInfoUtils.clearLoginState(); } catch (e) { /* ignore */ }
  try { Element.Message.error(tip); } catch (e) { /* ignore */ }

  // 已经在登录页就不用再刷新一次，避免打断用户正在输入的内容
  const hash = window.location.hash;
  if (!hash || hash === '#' || /#\/login/.test(hash)) {
    redirecting = false;
    return;
  }

  // 留一点时间让提示可见，再整体刷新到站点首页（如 http://localhost:8080/）
  // 路由里 '/' 挂的就是登录组件，重新加载整个应用后登录页会自动回填记住的账号密码
  setTimeout(() => {
    window.location.replace(
      window.location.origin + window.location.pathname
    );
  }, 600);
}

/**
 * 给 axios 实例挂上「登录态失效」响应拦截
 * 注意：无论判定结果如何，都会把响应/错误继续往下传，不影响各页面自己的错误处理
 */
export function registerAuthExpired(instance) {
  if (!instance || !instance.interceptors) return instance;

  instance.interceptors.response.use(
    res => {
      const url = res.config && res.config.url;
      const body = parseBody(res.data);
      if (!isAuthFreeApi(url) && isAuthExpiredBody(body)) {
        redirectToLogin(body.data || body.msg);
        return Promise.reject(new Error(body.message || body.msg || '登录态失效'));
      }
      return res;
    },
    err => {
      const resp = err.response;
      const url = (resp && resp.config && resp.config.url) || (err.config && err.config.url);
      const body = parseBody(resp && resp.data);
      // HTTP 401，或错误响应体里带 code = 401
      const expired = resp && Number(resp.status) === 401;
      if (!isAuthFreeApi(url) && (expired || isAuthExpiredBody(body))) {
        redirectToLogin((body && (body.message || body.msg)) || '暂未登录或登录已过期，请重新登录');
      }
      return Promise.reject(err);
    }
  );

  return instance;
}

export default registerAuthExpired;
